import React, { useState, useEffect } from 'react';
import { sideHustles } from '../lib/supabase';
import sideHustlesData from '../data/student-side-hustles.json';

export const StudentSideHustlesPage = ({ setPage, goBack, viewerMode = 'student' }) => {
  const [selectedHustle, setSelectedHustle] = useState(null);
  const [activeTab, setActiveTab] = useState('hustles');
  const [hustles, setHustles] = useState([]);
  const [loading, setLoading] = useState(true);

  const isStudent = viewerMode === 'student';

  // Load data from Supabase with fallback to JSON
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await sideHustles.getAll();
        if (!error && data && data.length > 0) {
          // Transform database data to match expected format
          setHustles(data.map(item => ({
            id: item.id,
            name: item.name,
            description: item.description,
            age_appropriate: item.age_appropriate,
            earning_potential: {
              range: `J$${item.earning_potential_min?.toLocaleString() || 0} - J$${item.earning_potential_max?.toLocaleString() || 0}`,
              frequency: item.earning_frequency
            },
            time_commitment: item.time_commitment,
            skills_needed: item.skills_needed || [],
            how_to_start: item.how_to_start || [],
            tips_for_success: item.tips_for_success || [],
            student_story: item.student_story,
            parent_note: item.parent_note,
            career_connection: item.career_connections || []
          })));
        } else {
          // Fallback to static JSON data
          setHustles(sideHustlesData.side_hustles);
        }
      } catch (err) {
        console.log('Using fallback JSON data');
        setHustles(sideHustlesData.side_hustles);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const tabs = [
    { id: 'hustles', name: 'Side Hustles', icon: '💰' },
    { id: 'safety', name: 'Safety First', icon: '🛡️' },
    { id: 'money', name: 'Money Management', icon: '🏦' },
    { id: 'balance', name: 'Work-Life Balance', icon: '⚖️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-3 sm:mb-4 text-brand-100 hover:text-white flex items-center gap-2 text-sm sm:text-base"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            {isStudent ? 'Student Side Hustles' : 'Safe Income Opportunities for Students'}
          </h1>
          <p className="text-brand-100 text-sm sm:text-base">
            {isStudent
              ? 'Safe, legal ways to make extra money while in school'
              : 'Vetted income opportunities appropriate for your child'}
          </p>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <p className="text-gray-700">
            {isStudent
              ? sideHustlesData.introduction.student_view
              : sideHustlesData.introduction.parent_view}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-1 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[100px] py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-brand-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hustles Tab */}
        {activeTab === 'hustles' && !selectedHustle && (
          loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading opportunities...</p>
            </div>
          ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hustles.map(hustle => (
              <div
                key={hustle.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedHustle(hustle)}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-2">{hustle.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{hustle.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-brand-600">💵</span>
                    <span>{hustle.earning_potential.range}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">⏰</span>
                    <span>{hustle.time_commitment}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">👤</span>
                    <span>Age {hustle.age_appropriate}</span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-1">
                  {hustle.career_connection.slice(0, 2).map((career, idx) => (
                    <span key={idx} className="text-xs bg-brand-100 text-brand-700 px-2 py-1 rounded">
                      {career}
                    </span>
                  ))}
                </div>

                <div className="mt-4 text-brand-600 font-medium text-sm">
                  Learn more →
                </div>
              </div>
            ))}
          </div>
          )
        )}

        {/* Selected Hustle Detail */}
        {activeTab === 'hustles' && selectedHustle && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <button
              onClick={() => setSelectedHustle(null)}
              className="mb-4 text-brand-600 hover:text-brand-800 flex items-center gap-2"
            >
              ← Back to all side hustles
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">{selectedHustle.name}</h2>
            <p className="text-gray-700 mb-6">{selectedHustle.description}</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Key Info */}
              <div className="bg-brand-50 rounded-lg p-4">
                <h3 className="font-semibold text-brand-800 mb-3">Key Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-600">Earning Potential:</span>
                    <div className="font-semibold text-brand-700">{selectedHustle.earning_potential.range}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Time Commitment:</span>
                    <div className="font-semibold">{selectedHustle.time_commitment}</div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Minimum Age:</span>
                    <div className="font-semibold">{selectedHustle.age_appropriate}</div>
                  </div>
                </div>
              </div>

              {/* Skills Needed */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-3">Skills You'll Need</h3>
                <ul className="space-y-2">
                  {selectedHustle.skills_needed.map((skill, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How to Start */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-3">How to Get Started</h3>
              <ol className="space-y-3">
                {selectedHustle.how_to_start.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Tips for Success */}
            <div className="mt-6 bg-yellow-50 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-3">Tips for Success</h3>
              <ul className="space-y-2">
                {selectedHustle.tips_for_success.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-600">★</span>
                    <span className="text-sm">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Student Story */}
            <div className="mt-6 bg-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800 mb-2">Real Student Success</h3>
              <p className="text-gray-700 italic">"{selectedHustle.student_story}"</p>
            </div>

            {/* Parent Note */}
            {!isStudent && (
              <div className="mt-6 bg-indigo-50 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">Note for Parents</h3>
                <p className="text-gray-700">{selectedHustle.parent_note}</p>
              </div>
            )}

            {/* Career Connections */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-3">Career Paths This Could Lead To</h3>
              <div className="flex flex-wrap gap-2">
                {selectedHustle.career_connection.map((career, idx) => (
                  <span key={idx} className="bg-brand-100 text-brand-700 px-3 py-2 rounded-lg">
                    {career}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Safety Tab */}
        {activeTab === 'safety' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {isStudent ? 'Staying Safe While Making Money' : 'Keeping Your Child Safe'}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-3">
                    {isStudent ? 'Your Safety Rules' : 'Safety Rules to Teach'}
                  </h3>
                  <ul className="space-y-2">
                    {(isStudent ? sideHustlesData.safety_guidelines.student_tips : sideHustlesData.safety_guidelines.parent_tips).map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-red-600">🛡️</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">What Counts as Safe Work</h3>
                  <ul className="space-y-2">
                    {sideHustlesData.legal_notes.what_counts_as_safe_work.map((work, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">✓</span>
                        {work}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Legal Notes */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Jamaica Child Labor Laws</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {sideHustlesData.legal_notes.jamaica_child_labor_laws.map((law, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                    {law}
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-2">Where to Report Problems</h3>
                <div className="space-y-2 text-sm">
                  <div><strong>Labor Issues:</strong> {sideHustlesData.legal_notes.where_to_report_problems.child_labor_violations}</div>
                  <div><strong>Exploitation Concerns:</strong> {sideHustlesData.legal_notes.where_to_report_problems.exploitation_concerns}</div>
                  <div><strong>Emergency:</strong> {sideHustlesData.legal_notes.where_to_report_problems.emergency}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Money Management Tab */}
        {activeTab === 'money' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Managing Your Money</h2>
              <div className="bg-brand-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-brand-800 mb-3">
                  {isStudent ? 'Tips for You' : 'Tips to Teach Your Child'}
                </h3>
                <ul className="space-y-2">
                  {(isStudent ? sideHustlesData.money_management.student_tips : sideHustlesData.money_management.parent_tips).map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-brand-600">💰</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              <h3 className="font-semibold text-gray-800 mb-3">Savings Goals Examples</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {sideHustlesData.money_management.saving_goals_examples.map((goal, idx) => (
                  <div key={idx} className="border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-brand-600">
                      J${goal.amount.toLocaleString()}
                    </div>
                    <div className="font-medium mt-2">{goal.goal}</div>
                    <div className="text-sm text-gray-600 mt-1">{goal.timeline}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Work-Life Balance Tab */}
        {activeTab === 'balance' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Balancing Work and School</h2>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-800 mb-3">Key Guidelines</h3>
                <ul className="space-y-2">
                  {sideHustlesData.balancing_work_and_school.guidelines.map((guideline, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-blue-600">📌</span>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-3">Warning Signs You're Overdoing It</h3>
                  <ul className="space-y-2">
                    {sideHustlesData.balancing_work_and_school.warning_signs.map((sign, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-red-600">⚠️</span>
                        {sign}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-brand-50 rounded-lg p-4">
                  <h3 className="font-semibold text-brand-800 mb-3">Time Management Tips</h3>
                  <ul className="space-y-2">
                    {sideHustlesData.balancing_work_and_school.time_management_tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-brand-600">✓</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
